/**
 * C1 · rate model on the web (pure): the rate behaviour the deal's configuration version gives (contract
 * `EffectiveRateSettings`, with the documented legacy fallback for older responses), how the rate and its type read
 * ("Profit rate 4.99% · Flat"), and the rate lines of the bank calculator ("Flat rate 4.99% — Source: Deal").
 * The server decides everything; nothing here invents a rate rule.
 */
import type { Schema } from '@dealdesk/api-schema';

import { fractionToPercent } from '@/shared/lib/money';

export type RateType = Schema<'RateType'>;
export type RateSettings = Schema<'EffectiveRateSettings'>;
export type RateContext = Schema<'CalculationRateContext'>;

export const RATE_TYPES = ['FLAT', 'REDUCING', 'DAILY_ACCRUAL'] as const satisfies readonly RateType[];
export const RATE_LABELS = ['profit_rate', 'annual_profit_rate', 'monthly_profit_rate', 'monthly_rate', 'interest_rate'] as const satisfies readonly Schema<'RateLabelCode'>[];
export const CALCULATOR_RATE_INPUTS = ['flatProfitRate', 'datedProfitRate'] as const satisfies readonly Schema<'CalculatorRateInput'>[];

/** The legacy fallback (same as the API) for a response that carries no rate settings. */
export function legacyRates(product: Schema<'ProductCode'>): RateSettings {
  const cc = product === 'CC';
  return {
    enabled: !cc,
    required: false,
    label: 'profit_rate',
    periodBasis: 'annual',
    allowedTypes: cc ? [] : [...RATE_TYPES],
    defaultType: null,
    calculatorMappings: cc ? [] : [{ rateType: 'FLAT', input: 'flatProfitRate' }],
    dailyAccrualIndependent: true,
    source: 'legacy',
  };
}

export const ratesOf = (form: { product: Schema<'ProductCode'>; rate?: RateSettings | undefined }): RateSettings => form.rate ?? legacyRates(form.product);

/**
 * How the rate type is entered: hidden (no rate), a fixed type shown as text next to the rate (one allowed type — less
 * noise than a one-option select), or a select limited to the allowed types.
 */
export function rateTypeMode(s: RateSettings): 'none' | 'fixed' | 'select' {
  if (!s.enabled || !s.allowedTypes.length) return 'none';
  return s.allowedTypes.length === 1 ? 'fixed' : 'select';
}

/** The rate type the form starts with: the deal's, else the version's default (shown, saved with the rate). */
export const initialRateType = (s: RateSettings, current: RateType | null | undefined): RateType | null => current ?? (s.enabled ? s.defaultType : null);

/** A percent string for display: two decimals at least, up to the four the rate may carry ("7.125" → "7.125"). */
export function percentDigits(v: string): number {
  const frac = v.split('.')[1]?.replace(/0+$/, '') ?? '';
  return Math.min(4, Math.max(2, frac.length));
}

/** The pieces of "Profit rate 4.99% · Flat" (null value = no rate). */
export interface RateDisplay {
  labelKey: string;
  value: string | null;
  typeKey: string | null;
}

export function rateDisplay(rate: { profitRate: string | null; rateType: RateType | null; label: Schema<'RateLabelCode'> }): RateDisplay {
  return { labelKey: `rate.label.${rate.label}`, value: rate.profitRate, typeKey: rate.profitRate && rate.rateType ? `rate.type.${rate.rateType}` : null };
}

// ================================================================================================ calculator

/** Business label key of a calculator rate input ("Flat rate", "Daily accrual rate"). */
export const calculatorRateKey = (input: string) => `rate.calculator.input.${input}`;

export interface CalculatorRateLine {
  input: Schema<'CalculatorRateInput'>;
  /** Percent ("4.99"), or null when the input is blank. */
  value: string | null;
  /** deal | default | manual | changed_from_deal | override. */
  source: 'deal' | 'default' | 'manual' | 'changed_from_deal' | 'override';
}

/** The rate lines of a calculation: every calculator rate with where its value comes from. */
export function calculatorRateLines(ctx: RateContext): CalculatorRateLine[] {
  return ctx.rates.map((r) => ({
    input: r.input,
    value: r.value === null ? null : fractionToPercent(r.value),
    source: r.changedFromDeal ? 'changed_from_deal' : r.source === 'deal' ? 'deal' : r.source === 'default' || r.source === 'version' ? 'default' : r.source === 'override' ? 'override' : 'manual',
  }));
}

/**
 * The deal-rate sentence: "Deal rate: 4.99% Flat" when the calculator uses it, or "The deal rate (7.25% Reducing) is
 * not used by this calculator." when no input is mapped for the deal's type. Null when the deal has no rate.
 */
export function dealRateNotice(ctx: RateContext): { kind: 'used' | 'unused'; rate: string; typeKey: string | null } | null {
  if (!ctx.dealRate) return null;
  return { kind: ctx.dealRateUsed ? 'used' : 'unused', rate: ctx.dealRate, typeKey: ctx.dealRateType ? `rate.type.${ctx.dealRateType}` : null };
}
